/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import { generateVisitorID } from "@cashfreepayments/deviceintel-js-sdk";
import makeRequest from "../../api/makeRequest";
import { MERCHANT_HELP_URL } from "../../constants/env";
import {
  validateForm,
  errorFormFieldHighlight,
  validFormFieldHighlight,
} from "./helper";

// Pages where popup SHOULD appear (whitelist approach)
const includedPaths = [
  "/virtual-payment-address",
  "/virtual-payment-address/",
  "/passport-verification-api",
  "/passport-verification-api/",
  "/recurring-payment",
  "/recurring-payment/",
];

// const MERCHANT_HELP_URL = "https://api.cashfree.com/integration/merchant/help";

const Loader = () => (
  <div className="flex items-center justify-center">
    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
  </div>
);

const SalesPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formErrorsData, setFormErrorsData] = useState({});
  const [showError, setShowError] = useState(false);
  const [showMsgError, setShowMsgError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if current page is in the whitelist
  const isIncludedPage = () => {
    // Safely access window object with optional chaining
    const currentPath =
      typeof window !== "undefined" ? window?.location?.pathname : "";

    // If we can't get the path, don't show the popup
    if (!currentPath) return false;

    // Check if current path exactly matches or is a child path of any included path
    return includedPaths.some(
      (path) =>
        currentPath === path ||
        currentPath === path.slice(0, -1) || // Handle trailing slash difference
        currentPath.startsWith(`${path}?`) || // Handle query parameters
        (path.endsWith("/") && currentPath.startsWith(path)),
    );
  };

  const wasPopupDismissedToday = () => {
    const today = new Date().toISOString().split("T")[0]; // Get today's date
    const dismissedDate = localStorage.getItem("popupDismissedDate");
    return dismissedDate === today;
  };

  const wasFormEverSubmitted = () => {
    return localStorage.getItem("formSubmitted") === "true";
  };

  const updateVisitCount = () => {
    // Don't update visit count if form was submitted or popup was dismissed or not on included page
    if (
      wasFormEverSubmitted() ||
      wasPopupDismissedToday() ||
      !isIncludedPage()
    ) {
      return 0;
    }

    const today = new Date().toISOString().split("T")[0];
    const lastVisitDate = localStorage.getItem("lastVisitDate");
    let visitCount = parseInt(localStorage.getItem("visitCount") || "0", 10);

    if (lastVisitDate !== today) {
      visitCount = 1;
      localStorage.setItem("visitCount", "1");
      localStorage.setItem("lastVisitDate", today);
    } else {
      visitCount += 1;
      localStorage.setItem("visitCount", visitCount.toString());
    }

    return visitCount;
  };

  const showPopupWithAnimation = () => {
    if (
      wasFormEverSubmitted() ||
      wasPopupDismissedToday() ||
      !isIncludedPage()
    ) {
      return;
    }
    setShowPopup(true);
    setTimeout(() => {
      setPopupVisible(true);
    }, 50);
  };

  const hidePopupWithAnimation = () => {
    setPopupVisible(false);
    setTimeout(() => {
      setShowPopup(false);
    }, 300);
  };

  const startPopupTimer = () => {
    if (
      wasFormEverSubmitted() ||
      wasPopupDismissedToday() ||
      !isIncludedPage()
    ) {
      return;
    }

    setTimeout(() => {
      showPopupWithAnimation();
    }, 15000); // Show popup after 15 seconds
  };

  const initVisitorTracking = async () => {
    try {
      // First check if we're on an included page
      if (!isIncludedPage()) {
        return;
      }

      // Secondary check to prevent any initialization if form was submitted
      if (wasFormEverSubmitted() || wasPopupDismissedToday()) {
        // Ensure popup is not shown if form was submitted
        setShowPopup(false);
        setPopupVisible(false);
        return;
      }

      const visitorId = await generateVisitorID();
      const storedVisitorId = localStorage.getItem("visitorId");
      let visitCount = updateVisitCount();

      // If popup was dismissed today or form was submitted, visitCount will be 0
      if (visitCount === 0) {
        return;
      }

      if (!storedVisitorId || storedVisitorId !== visitorId) {
        localStorage.setItem("visitorId", visitorId);
        visitCount = 1;
        localStorage.setItem("visitCount", "1");
      }

      if (visitCount >= 3) {
        showPopupWithAnimation(); // Show popup immediately on 3rd visit
      } else {
        startPopupTimer(); // Show popup after 15 seconds on 1st and 2nd visits
      }
    } catch (error) {
      console.error("Error initializing visitor tracking:", error);
    }
  };

  useEffect(() => {
    // First check if we're on an included page
    if (!isIncludedPage()) {
      return;
    }

    // Secondary check - don't even initialize if form was submitted or popup was dismissed
    if (wasFormEverSubmitted() || wasPopupDismissedToday()) {
      // Ensure popup is hidden if form was submitted
      setShowPopup(false);
      setPopupVisible(false);
      return;
    }
    initVisitorTracking();
  }, []);

  const handleOnFocus = () => {
    setShowError(false);
    setFormErrorsData({});
    setShowMsgError("");
  };

  const handleSubmitData = (values, actions) => {
    const formErrors = validateForm(values);
    setFormErrorsData(formErrors);

    if (Object.values(formErrors).length > 0) {
      setShowError(true);
      return;
    }

    const { name, email, phone } = values;

    const dataObj = {
      action: "shopifyLogin",
      name,
      email,
      phone,
      entity: "I am Exploring Cashfree",
      message: `Sales team connection request from website link ${window?.location?.pathname}`,
      source: "Contact_us_from_pop_up",
    };

    setShowError(false);
    setFormErrorsData({});
    setIsSubmitting(true);

    makeRequest
      .post(MERCHANT_HELP_URL, dataObj)
      .then((response) => {
        if (response.status === 200) {
          // Permanently store that form was submitted successfully
          localStorage.setItem("formSubmitted", "true");
          setFormSubmitted(true);
          actions.resetForm();
        }
      })
      .catch(() => {
        setShowMsgError("An error occurred. Please try again.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  useEffect(() => {
    if (formSubmitted) {
      // After showing success message, hide the popup
      setTimeout(() => {
        hidePopupWithAnimation();
        // We do NOT reset formSubmitted in localStorage, only the state
        setFormSubmitted(false);
      }, 6000);
    }
  }, [formSubmitted]);

  // Check if form was ever submitted before initializing
  useEffect(() => {
    // If form was ever submitted, don't show the popup again
    if (localStorage.getItem("formSubmitted") === "true") {
      setShowPopup(false);
      setPopupVisible(false);
    }
  }, []);

  const handleClosePopup = () => {
    // Save the current date when popup is dismissed
    const today = new Date().toISOString().split("T")[0];
    localStorage.setItem("popupDismissedDate", today);

    // Reset visit count as well
    localStorage.setItem("visitCount", "0");
    localStorage.setItem("lastVisitDate", "");

    hidePopupWithAnimation();
  };

  // If not on an included page, don't render anything
  if (!isIncludedPage()) {
    return null;
  }

  return (
    <>
      {showPopup && (
        <div className="fixed inset-0 z-[9998] bg-transparent pointer-events-none">
          <div
            className={`fixed bottom-0 left-0 w-full md:bottom-4 md:right-4 md:left-auto md:w-[24rem] transform transition-all duration-300 ease-in-out ${
              popupVisible
                ? "opacity-100 translate-y-0 scale-100"
                : "opacity-0 translate-y-16 scale-95"
            }`}
            style={{ zIndex: 9999, pointerEvents: "auto" }}>
            <div className="bg-white rounded-t-lg md:rounded-lg shadow-2xl shadow-purple-500/30 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:shadow-purple-500/40 w-full">
              <div className={formSubmitted ? "p-1" : "p-5"}>
                {!formSubmitted ? (
                  <>
                    <div className="flex justify-between items-center pb-2">
                      <h3 className="text-m font-heading font-semibold">
                        Contact Us
                      </h3>
                      <button
                        type="button"
                        onClick={handleClosePopup}
                        className="text-gray-400 hover:text-gray-600 focus:outline-none"
                        aria-label="Close popup">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <p className="text-sm mb-3 font-body">
                      Fill out this form and we&apos;ll get back to you shortly.
                    </p>

                    <div className="text-sm font-semibold">
                      <Formik
                        initialValues={{
                          name: "",
                          email: "",
                          phone: "",
                        }}
                        onSubmit={handleSubmitData}>
                        {({
                          handleSubmit,
                          handleChange,
                          handleBlur,
                          values,
                        }) => (
                          <Form onSubmit={handleSubmit}>
                            <div className="mb-[24px]">
                              <label
                                htmlFor="name"
                                className="block mb-2 text-sm text-cf-hero font-body font-semibold">
                                Your Name
                              </label>
                              <Field
                                id="name"
                                name="name"
                                placeholder="Rahul Sharma"
                                type="text"
                                className={
                                  formErrorsData.name
                                    ? errorFormFieldHighlight
                                    : validFormFieldHighlight
                                }
                                autoComplete="off"
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onFocus={handleOnFocus}
                              />

                              {formErrorsData.name && (
                                <p className="font-light text-cf-red-400">
                                  {formErrorsData.name}
                                </p>
                              )}
                            </div>

                            <div className="mb-[24px]">
                              <label
                                htmlFor="email"
                                className="block mb-2 text-sm text-cf-hero font-body font-semibold">
                                Email ID
                              </label>
                              <Field
                                type="email"
                                id="email"
                                className={
                                  formErrorsData.email
                                    ? errorFormFieldHighlight
                                    : validFormFieldHighlight
                                }
                                placeholder="rahulsharma@cashfree.com"
                                name="email"
                                autoComplete="off"
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                onFocus={handleOnFocus}
                              />

                              {formErrorsData.email && (
                                <p className="font-light text-cf-red-400">
                                  {formErrorsData.email}
                                </p>
                              )}
                            </div>

                            <div className="mb-[24px]">
                              <label
                                htmlFor="phone"
                                className="relative block mb-2 text-sm text-cf-hero font-body font-semibold">
                                Phone Number
                                <div className="flex mt-1 relative">
                                  <div className="flex items-center justify-center absolute left-0 top-0 bottom-0 w-12 text-cf-hero">
                                    <p className="text-center font-body">+91</p>
                                    <div className="h-6 w-px bg-gray-300 ml-2" />
                                  </div>
                                  <Field
                                    type="tel"
                                    id="phone"
                                    className={`${
                                      formErrorsData.phone
                                        ? errorFormFieldHighlight
                                        : validFormFieldHighlight
                                    } pl-12`}
                                    placeholder="9793067777"
                                    name="phone"
                                    maxLength={10}
                                    autoComplete="off"
                                    value={values.phone}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    onFocus={handleOnFocus}
                                  />
                                </div>
                              </label>
                              {formErrorsData.phone && (
                                <p className="font-light text-cf-red-400">
                                  {formErrorsData.phone}
                                </p>
                              )}
                            </div>

                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className={`button
                                  hover:scale-[1.02]
                                  active:scale-[0.98]
                                  !border-none
                                  !rounded-[5px]
                                  inline-block 
                                  w-full text-white 
                                  items-center justify-center 
                                  text-[14px] 
                                  font-body
                                  font-semibold 
                                  bg-[#6933d3] border
                                 !border-[#dcdee6]
                                  py-3 
                                   transition-transform duration-150 ease-in-out
                                  ${
                                    isSubmitting
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }`}
                              style={{ backgroundColor: "#6933d3" }}>
                              {isSubmitting ? <Loader /> : "Submit"}
                            </button>

                            {showError && (
                              <div className="mt-3 text-cf-red-400 font-body">
                                Please fill the form completely
                              </div>
                            )}

                            {showMsgError?.length > 0 && (
                              <div className="mt-3 text-cf-red-400 font-body">
                                {showMsgError}
                              </div>
                            )}
                          </Form>
                        )}
                      </Formik>
                    </div>
                  </>
                ) : (
                  <div className="text-[#240253] flex font-body font-medium text-[13px] bg-[#05C16E2E] p-3 rounded-md">
                    <div className="pr-[8px]">✅</div>
                    Thank you! Our sales team will be in touch with you shortly.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SalesPopup;
